import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { allocations } from "../../api/endpoints/allocations";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/shell/PageHeader";
import { GateBanner } from "./GateBanner";
import { AllocationTree } from "./AllocationTree";
import { NodeDetailPanel } from "./NodeDetailPanel";
import { ImportRunnerPanel } from "./ImportRunnerPanel";
import { useAllocationTree } from "./useAllocationTree";
import { useIsPlatformAdmin } from "../auth/useIsPlatformAdmin";
import type { AllocNode } from "./buildAllocationTree";

function findNode(node: AllocNode, key: string): AllocNode | null {
  if (node.key === key) return node;
  for (const c of node.children) {
    const hit = findNode(c, key);
    if (hit) return hit;
  }
  return null;
}

export function AllocationConsole({
  node, onNodeChange,
}: {
  node: string | null; onNodeChange: (key: string | null) => void;
}) {
  const qc = useQueryClient();
  const canWrite = useIsPlatformAdmin();
  const [importsOpen, setImportsOpen] = useState(false);
  const gate = useQuery({
    ...adminQueryOptions,
    queryKey: qk.allocationGate(),
    queryFn: () => allocations.gate().then((r) => r.data),
  });
  const { tree, isPending, error } = useAllocationTree();
  const gateOk = gate.data?.status === "ok";
  // The tree memo composes as soon as accounts/ledger/template load — before the
  // tenant/site fan-out settles, and even when a nested fan-out query has failed.
  // Don't surface a half-loaded estate as actionable: require a settled, error-free load.
  const ready = !isPending && !error && tree != null;
  const selected = !isPending && !error && tree && node ? findNode(tree, node) : null;

  return (
    <>
      <PageHeader title="Allocation" subtitle="IP allocation across the 10.128.0.0/9 supernet — allocate account/site CIDRs and generate VLANs/DHCP." />
      {gate.data && <GateBanner gate={gate.data} onOpenImports={() => setImportsOpen(true)} />}
      {error ? <ErrorBanner error={error} title="Failed to load allocation tree" /> : null}
      {error ? null : !ready ? (
        <p style={{ color: "var(--text-dim)" }}>Loading allocation estate…</p>
      ) : (
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ borderRight: "1px solid var(--border)", paddingRight: 12 }}>
            {tree && <AllocationTree root={tree} selectedKey={node} onSelect={onNodeChange} />}
          </div>
          <div style={{ flex: 1 }}>
            <NodeDetailPanel
              node={selected}
              canWrite={canWrite}
              gateOk={gateOk}
              onChanged={() => void qc.invalidateQueries({ queryKey: qk.allocationLedger() })}
            />
          </div>
        </div>
      )}
      <Modal open={importsOpen} onClose={() => setImportsOpen(false)} title="Legacy import runner">
        <ImportRunnerPanel canWrite={canWrite} gateLatestRunId={gate.data?.latest_run_id ?? null} />
      </Modal>
    </>
  );
}
